/**
 * buddyBodyStage — 3-stage body evolution system.
 * Maps ONLY long-term macro state to one of three body forms:
 *   BittyB (base), BitGuard (mid), Ragnarok-B (final).
 *
 * Evolution is driven exclusively by the macro advisor state (displayMacroState),
 * which already has EMA smoothing + hysteresis built in.
 * Short-term moods and heatLevel do NOT affect body stage to prevent rapid flip-flopping.
 *
 * Additional hysteresis: the stage only changes after the new stage has been
 * consistently indicated for HYSTERESIS_DURATION_MS (default 60s).
 */

import bodyBittyB from '@/assets/buddy/layers/body-bittyb.webp';
import bodyBitGuard from '@/assets/buddy/layers/body-bitguard.webp';
import bodyRagnarokB from '@/assets/buddy/layers/body-ragnarokb.webp';

export type BodyStage = 'bittyb' | 'bitguard' | 'ragnarokb';

export const BODY_STAGE_ASSETS: Record<BodyStage, string> = {
  bittyb: bodyBittyB,
  bitguard: bodyBitGuard,
  ragnarokb: bodyRagnarokB,
};

/**
 * Per-stage face positioning + render scale.
 *
 * IMPORTANT: svgScale is calibrated so that the ABSOLUTE face size
 * (svgScale × renderScale) is identical across all stages.
 * Target effective face scale = 0.92 (BittyB baseline).
 *
 *   BittyB:     0.92 / 1.00 = 0.92
 *   BitGuard:   0.92 / 1.05 ≈ 0.876
 *   Ragnarok-B: 0.92 / 1.30 ≈ 0.708
 */
export const BODY_STAGE_FACE_OFFSET: Record<BodyStage, {
  svgTranslateY: number;
  svgScale: number;
  svgOriginX: number;
  svgOriginY: number;
  renderScale: number;
}> = {
  bittyb:    { svgTranslateY: -10,  svgScale: 0.92,  svgOriginX: 200, svgOriginY: 182, renderScale: 1.0 },
  bitguard:  { svgTranslateY: -24,  svgScale: 0.876, svgOriginX: 200, svgOriginY: 182, renderScale: 1.05 },
  ragnarokb: { svgTranslateY: -90,  svgScale: 0.708, svgOriginX: 200, svgOriginY: 182, renderScale: 1.30 },
};

/** All stage assets for preloading */
export const ALL_BODY_STAGE_ASSETS = Object.values(BODY_STAGE_ASSETS);

/**
 * Determine the RAW (unfiltered) body stage from macro state only.
 * No short-term mood or heatLevel — those change too fast.
 *
 * BittyB:     DEEP_VALUE, ACCUMULATION, NEUTRAL (calm macro)
 * BitGuard:   WARM, MIXED (tension rising)
 * Ragnarok-B: HOT (extreme macro heat)
 */
export function selectBodyStageRaw(
  displayMacroState: string | null | undefined,
): BodyStage {
  const macro = (displayMacroState ?? '').toUpperCase();

  if (macro === 'HOT') {
    return 'ragnarokb';
  }

  if (macro === 'WARM' || macro === 'MIXED') {
    return 'bitguard';
  }

  // DEEP_VALUE, ACCUMULATION, NEUTRAL, or unknown
  return 'bittyb';
}

// ── Hysteresis gate ──
// Prevents rapid stage changes by requiring the new stage to be
// consistently indicated for at least this duration before committing.
const HYSTERESIS_DURATION_MS = 60_000; // 60 seconds

let _committedStage: BodyStage = 'bittyb';
let _candidateStage: BodyStage | null = null;
let _candidateSince: number = 0;

/**
 * selectBodyStage — the primary export used by BuddyCharacter.
 * Wraps selectBodyStageRaw with a time-based hysteresis gate.
 *
 * - If the raw stage matches the current committed stage → return it immediately.
 * - If it differs, start a candidate timer. Only commit once the candidate
 *   has been consistently requested for HYSTERESIS_DURATION_MS.
 * - If the raw stage flips back before the timer expires, the candidate resets.
 *
 * shortTermMood and heatLevel are accepted for API compatibility but ignored.
 */
export function selectBodyStage(
  displayMacroState: string | null | undefined,
  _shortTermMood?: string,
  _heatLevel?: number,
): BodyStage {
  const raw = selectBodyStageRaw(displayMacroState);
  const now = Date.now();

  // Already in the right stage
  if (raw === _committedStage) {
    _candidateStage = null;
    _candidateSince = 0;
    return _committedStage;
  }

  // New candidate or same candidate continuing
  if (raw === _candidateStage) {
    // Has enough time passed?
    if (now - _candidateSince >= HYSTERESIS_DURATION_MS) {
      _committedStage = raw;
      _candidateStage = null;
      _candidateSince = 0;
      return _committedStage;
    }
    // Not yet — keep waiting
    return _committedStage;
  }

  // Different candidate — reset timer
  _candidateStage = raw;
  _candidateSince = now;
  return _committedStage;
}
